import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCircle, Phone, MapPin, Heart, LogOut, CheckCircle, Loader2, User, Mail, Calendar, Users } from 'lucide-react';
interface FloatingLabelInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  icon?: React.ReactNode;
}
const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  icon
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value && value.length > 0;
  // Date inputs always show browser placeholder text, so label must always float
  const isDateType = type === 'date';
  const shouldFloat = isFocused || hasValue || isDateType;
  return <div className="relative" data-id="element-4777">
      <input type={type} value={value} onChange={onChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} required={required} className={`w-full px-4 py-3.5 pt-6 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all duration-200 outline-none ${icon ? 'pr-12' : ''} ${isDateType ? 'cursor-pointer' : ''}`} placeholder=" " data-id="element-4778" />
      <label className={`absolute left-4 transition-all duration-200 pointer-events-none ${shouldFloat ? 'top-2 text-xs font-medium text-teal-600' : 'top-1/2 -translate-y-1/2 text-gray-500'}`} data-id="element-4779">
        {label}
        {required && <span className="text-rose-500 ml-0.5" data-id="element-4780">*</span>}
      </label>
      {icon && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-4781">
          {icon}
        </div>}
    </div>;
};
const PatientSelfRegisterPage: React.FC = () => {
  const {
    user,
    logout
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [existingPatient, setExistingPatient] = useState<any>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    date_of_birth: '',
    gender: '',
    identification_number: '',
    phone: '',
    address: '',
    city: '',
    state: ''
  });
  const [emergencyContact, setEmergencyContact] = useState({
    name: '',
    relationship: '',
    phone: ''
  });
  useEffect(() => {
    const checkExistingPatient = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }
      try {
        const {
          data,
          error: queryError
        } = await supabase.from('patients').select('*').eq('email', user.email).maybeSingle();
        if (queryError) {
          console.error('Error checking existing patient:', queryError);
          setLoading(false);
          return;
        }
        if (data) {
          setAlreadyRegistered(true);
          setExistingPatient(data);
        }
      } catch (err) {
        console.error('Error in checkExistingPatient:', err);
      } finally {
        setLoading(false);
      }
    };
    checkExistingPatient();
  }, [user?.email]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.date_of_birth || !formData.gender || !formData.phone) {
        setError('Por favor complete todos los campos obligatorios');
        setSubmitting(false);
        return;
      }
      const {
        data: insertedPatient,
        error: insertError
      } = await supabase.from('patients').insert([{
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender.toLowerCase(),
        identification_number: formData.identification_number || null,
        phone: formData.phone,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        emergency_contact: emergencyContact,
        created_at: new Date().toISOString()
      }]).select().single();
      if (insertError) {
        console.error('Supabase insert error:', insertError);
        setError(`Error al registrar: ${insertError.message}`);
        setSubmitting(false);
        return;
      }
      try {
        await supabase.from('audit_logs').insert([{
          user_id: user?.id || 'paciente-system',
          user_name: user?.name || 'Sistema de Registro de Pacientes',
          action: 'create',
          module: 'patients',
          entity_id: insertedPatient.id,
          entity_name: `${formData.first_name} ${formData.last_name}`,
          details: `Paciente registrado mediante auto-registro: ${formData.first_name} ${formData.last_name} (${formData.phone})`,
          created_at: new Date().toISOString()
        }]);
      } catch (auditError) {
        console.error('Error creating audit log:', auditError);
      }
      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al registrar paciente');
    } finally {
      setSubmitting(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center" data-id="element-4782">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="text-center" data-id="element-4783">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" data-id="element-4784" />
          <p className="text-gray-600 font-medium" data-id="element-4785">Cargando...</p>
        </motion.div>
      </div>;
  }
  if (alreadyRegistered && existingPatient) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4" data-id="element-4786">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-teal-100" data-id="element-4787">
          <div className="text-center mb-6" data-id="element-4788">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4789">
              <CheckCircle className="w-10 h-10 text-white" data-id="element-4790" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4791">
              Ya Estás Registrado
            </h2>
            <p className="text-gray-600" data-id="element-4792">
              Tu información ya está en nuestro sistema
            </p>
          </div>

          <div className="bg-teal-50 rounded-xl p-6 mb-6 space-y-3" data-id="element-4793">
            <div className="flex items-center gap-3" data-id="element-4794">
              <User className="w-5 h-5 text-teal-600" data-id="element-4795" />
              <div data-id="element-4796">
                <p className="text-sm text-gray-600" data-id="element-4797">Nombre</p>
                <p className="font-semibold text-gray-900" data-id="element-4798">
                  {existingPatient.first_name || existingPatient.name}{' '}
                  {existingPatient.last_name || ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-4799">
              <Phone className="w-5 h-5 text-teal-600" data-id="element-4800" />
              <div data-id="element-4801">
                <p className="text-sm text-gray-600" data-id="element-4802">Teléfono</p>
                <p className="font-semibold text-gray-900" data-id="element-4803">
                  {existingPatient.phone}
                </p>
              </div>
            </div>
          </div>

          <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" data-id="element-4804">
            <LogOut className="w-5 h-5" data-id="element-4805" />
            Cerrar Sesión
          </button>
        </motion.div>
      </div>;
  }
  if (success) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4" data-id="element-4806">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-teal-100" data-id="element-4807">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200
        }} className="text-center mb-6" data-id="element-4808">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4809">
              <CheckCircle className="w-10 h-10 text-white" data-id="element-4810" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4811">
              ¡Registro Exitoso!
            </h2>
            <p className="text-gray-600" data-id="element-4812">
              Tu información ha sido guardada correctamente
            </p>
          </motion.div>

          <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" data-id="element-4813">
            <LogOut className="w-5 h-5" data-id="element-4814" />
            Cerrar Sesión
          </button>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50" data-id="element-4815">
      <motion.div initial={{
      y: -20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-8 px-4 shadow-lg" data-id="element-4816">
        <div className="max-w-4xl mx-auto" data-id="element-4817">
          <div className="flex items-center gap-4 mb-2" data-id="element-4818">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center" data-id="element-4819">
              <UserCircle className="w-8 h-8" data-id="element-4820" />
            </div>
            <div data-id="element-4821">
              <h1 className="text-3xl font-bold" data-id="element-4822">Registro de Paciente</h1>
              <p className="text-teal-100 mt-1" data-id="element-4823">
                Complete su información personal
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-4824">
        <motion.form initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} onSubmit={handleSubmit} className="space-y-6" data-id="element-4825">
          <AnimatePresence data-id="element-4826">
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center gap-3" data-id="element-4827">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-4828"></div>
                {error}
              </motion.div>}
          </AnimatePresence>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-4829">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 flex items-center gap-3" data-id="element-4830">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-4831">
                <User className="w-5 h-5 text-white" data-id="element-4832" />
              </div>
              <div data-id="element-4833">
                <h2 className="text-xl font-bold text-white" data-id="element-4834">
                  Información Personal
                </h2>
                <p className="text-teal-50 text-sm" data-id="element-4835">
                  Datos básicos del paciente
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4836">
              <FloatingLabelInput label="Nombre" value={formData.first_name} onChange={e => setFormData({
              ...formData,
              first_name: e.target.value
            })} required icon={<User className="w-5 h-5" data-id="element-4838" />} data-id="element-4837" />
              <FloatingLabelInput label="Apellido" value={formData.last_name} onChange={e => setFormData({
              ...formData,
              last_name: e.target.value
            })} required icon={<User className="w-5 h-5" data-id="element-4840" />} data-id="element-4839" />
              <FloatingLabelInput label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} required icon={<Mail className="w-5 h-5" data-id="element-4842" />} data-id="element-4841" />
              <FloatingLabelInput label="Fecha de Nacimiento" type="date" value={formData.date_of_birth} onChange={e => setFormData({
              ...formData,
              date_of_birth: e.target.value
            })} required icon={<Calendar className="w-5 h-5" data-id="element-4844" />} data-id="element-4843" />

              <div className="relative" data-id="element-4845">
                <select value={formData.gender} onChange={e => setFormData({
                ...formData,
                gender: e.target.value
              })} required className="w-full px-4 py-3.5 pt-6 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all duration-200 appearance-none bg-white outline-none" data-id="element-4846">
                  <option value="" data-id="element-4847">Seleccionar...</option>
                  <option value="masculino" data-id="element-4848">Masculino</option>
                  <option value="femenino" data-id="element-4849">Femenino</option>
                  <option value="otro" data-id="element-4850">Otro</option>
                </select>
                <label className="absolute left-4 top-2 text-xs font-medium text-teal-600 transition-all duration-200 pointer-events-none" data-id="element-4851">
                  Género<span className="text-rose-500 ml-0.5" data-id="element-4852">*</span>
                </label>
                <Users className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-4853" />
              </div>

              <FloatingLabelInput label="Número de Identificación" value={formData.identification_number} onChange={e => setFormData({
              ...formData,
              identification_number: e.target.value
            })} icon={<UserCircle className="w-5 h-5" data-id="element-4855" />} data-id="element-4854" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-4856">
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 px-6 py-4 flex items-center gap-3" data-id="element-4857">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-4858">
                <Phone className="w-5 h-5 text-white" data-id="element-4859" />
              </div>
              <div data-id="element-4860">
                <h2 className="text-xl font-bold text-white" data-id="element-4861">
                  Información de Contacto
                </h2>
                <p className="text-cyan-50 text-sm" data-id="element-4862">
                  Datos de ubicación y contacto
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4863">
              <FloatingLabelInput label="Teléfono" type="tel" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} required icon={<Phone className="w-5 h-5" data-id="element-4865" />} data-id="element-4864" />
              <FloatingLabelInput label="Dirección" value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-4867" />} data-id="element-4866" />
              <FloatingLabelInput label="Ciudad" value={formData.city} onChange={e => setFormData({
              ...formData,
              city: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-4869" />} data-id="element-4868" />
              <FloatingLabelInput label="Estado/Provincia" value={formData.state} onChange={e => setFormData({
              ...formData,
              state: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-4871" />} data-id="element-4870" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-4872">
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-6 py-4 flex items-center gap-3" data-id="element-4873">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-4874">
                <Heart className="w-5 h-5 text-white" data-id="element-4875" />
              </div>
              <div data-id="element-4876">
                <h2 className="text-xl font-bold text-white" data-id="element-4877">
                  Contacto de Emergencia
                </h2>
                <p className="text-rose-50 text-sm" data-id="element-4878">
                  Persona a contactar en caso de emergencia
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4879">
              <FloatingLabelInput label="Nombre del Contacto" value={emergencyContact.name} onChange={e => setEmergencyContact({
              ...emergencyContact,
              name: e.target.value
            })} icon={<User className="w-5 h-5" data-id="element-4881" />} data-id="element-4880" />
              <FloatingLabelInput label="Relación" value={emergencyContact.relationship} onChange={e => setEmergencyContact({
              ...emergencyContact,
              relationship: e.target.value
            })} icon={<Users className="w-5 h-5" data-id="element-4883" />} data-id="element-4882" />
              <FloatingLabelInput label="Teléfono de Emergencia" type="tel" value={emergencyContact.phone} onChange={e => setEmergencyContact({
              ...emergencyContact,
              phone: e.target.value
            })} icon={<Phone className="w-5 h-5" data-id="element-4885" />} data-id="element-4884" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="flex gap-4" data-id="element-4886">
            <button type="submit" disabled={submitting} className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4887">
              {submitting ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-4888" />
                  Registrando...
                </> : <>
                  <CheckCircle className="w-5 h-5" data-id="element-4889" />
                  Registrar Paciente
                </>}
            </button>

            <button type="button" onClick={logout} className="px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center gap-2" data-id="element-4890">
              <LogOut className="w-5 h-5" data-id="element-4891" />
              Salir
            </button>
          </motion.div>
        </motion.form>
      </div>
    </div>;
};
export { PatientSelfRegisterPage };
export default PatientSelfRegisterPage;